import { useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEnqueueAutomationMove } from './useEnqueueAutomationMove';
import {
  useDispatchSessionStore,
  type PendingDispatchSession,
} from './useDispatchSessionStore';
import { buildEnqueueInputFromExecutions } from './dispatchSession.helpers';
import {
  searchExecutionsByDispatchSession,
  type DispatchSessionSearchResult,
} from './workflowExecutions.api';

const POLL_INTERVAL_MS = 1500;
// Backstop if `dispatch_ready` never flips (BE hiccup / Redis TTL) — stop
// polling and enqueue whatever was last fetched.
const MAX_WAIT_MS = 60_000;

const dispatchSessionKey = (sessionId: string) =>
  ['dispatchSession', sessionId] as const;

// One poller per pending session: polls the session-scoped search until BE
// reports `dispatch_ready`, then enqueues the popup and retires the session.
// Mounted by DispatchSessionWatcher — never directly.
export const DispatchSessionPoller = ({
  session,
}: {
  session: PendingDispatchSession;
}) => {
  const enqueue = useEnqueueAutomationMove();
  const removeSession = useDispatchSessionStore((s) => s.removeSession);

  // Guards against the ready-path and deadline timer both firing.
  const handledRef = useRef(false);

  const finish = useCallback(
    (result: DispatchSessionSearchResult | undefined) => {
      if (handledRef.current) return;
      handledRef.current = true;
      const input = result
        ? buildEnqueueInputFromExecutions(result.executions)
        : null;
      // Order by when the bulk move was initiated (session start), not when
      // this poller resolved — otherwise a slow-resolving session would jump
      // above more-recent moves. See the ordering note in useUnderwayStore.
      if (input) enqueue({ ...input, initiatedAt: session.startedAt });
      removeSession(session.sessionId);
    },
    [enqueue, removeSession, session.sessionId, session.startedAt]
  );

  const { data } = useQuery({
    queryKey: dispatchSessionKey(session.sessionId),
    queryFn: () => searchExecutionsByDispatchSession(session.sessionId),
    // Stop polling once ready, once handled, or past the deadline.
    refetchInterval: (query) => {
      if (handledRef.current) return false;
      if (query.state.data?.dispatchReady) return false;
      if (Date.now() - session.startedAt > MAX_WAIT_MS) return false;
      return POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: true,
    staleTime: 0,
    gcTime: 0,
  });

  // Latest fetch, read by the deadline timer without re-arming it.
  const dataRef = useRef<DispatchSessionSearchResult | undefined>(undefined);
  dataRef.current = data;

  // Resolve as soon as BE reports the executions are final.
  useEffect(() => {
    if (data?.dispatchReady) finish(data);
  }, [data, finish]);

  // Deadline: armed once (elapsed-adjusted so it survives a refresh).
  useEffect(() => {
    const remaining = Math.max(
      0,
      MAX_WAIT_MS - (Date.now() - session.startedAt)
    );
    const timer = setTimeout(() => finish(dataRef.current), remaining);
    return () => clearTimeout(timer);
  }, [session.startedAt, finish]);

  return null;
};
